import { ReactNode, useState } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { loginRequest } from '../auth/authConfig';
import Button from './Button';

const MicrosoftLogo = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 23 23" fill="none">
    <rect x="1" y="1" width="10" height="10" fill="#F25022"/>
    <rect x="12" y="1" width="10" height="10" fill="#7FBA00"/>
    <rect x="1" y="12" width="10" height="10" fill="#00A4EF"/>
    <rect x="12" y="12" width="10" height="10" fill="#FFB900"/>
  </svg>
);

interface LoginFormProps {
  children: ReactNode;
}

const LoginForm = ({ children }: LoginFormProps) => {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress, instance } = useMsal();
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async () => {
    try {
      setIsLoading(true);
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login failed:', error);
      setIsLoading(false);
    }
  };

  if (inProgress === 'handleRedirect') {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="inline-block w-8 h-8 border-2 border-border-secondary border-t-semantic-info rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-lg shadow-xl border border-border-secondary overflow-hidden">
            {/* Header */}
            <div className="px-8 pt-8 pb-6 text-center border-b border-gray-100">
              <img 
                src="/morgan-morgan-logo.png" 
                alt="Morgan & Morgan Logo" 
                className="h-12 mx-auto mb-4 object-contain"
              />
              <h1 className="text-2xl font-semibold text-gray-900 mb-2">UniFi Analyzer</h1>
              <p className="text-sm text-gray-600">Secure document analysis platform</p>
            </div>
            
            {/* Content */}
            <div className="px-8 py-6">
              <div className="text-center mb-6">
                <h2 className="text-lg font-medium text-gray-900 mb-2">Sign in to continue</h2>
                <p className="text-sm text-gray-600">
                  Please sign in with your Morgan & Morgan account to access UniFi.
                </p>
              </div>

              <div className="space-y-4">
                <Button
                  variant="outline"
                  size="md"
                  loading={isLoading}
                  icon={!isLoading && <MicrosoftLogo />}
                  onClick={handleLogin}
                  className="w-full"
                >
                  {isLoading ? 'Signing in...' : 'Log in with Microsoft'}
                </Button>
              </div>
            </div>

            {/* Footer */}
            <div className="px-8 py-4 bg-gray-50 border-t border-gray-100">
              <p className="text-xs text-center text-gray-500">
                Access restricted to authorized Morgan & Morgan employees only.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default LoginForm;
